import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Box, TextField, Button, Typography, IconButton, Tooltip} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {InsightsChatMessage} from '../../features/Insights/renderInsightsChatMessage';
import {
  addMessage,
  clearChatHistory,
  setPanelFrame,
  setChatOpen,
  setHistoryPanelOpen,
  setActiveThreadId,
  loadThread,
  setChatOverlayDimEnabled,
  setTextDefinitionMode,
} from '../../features/Insights/chatSlice';
import {useInsightsChatSend} from '../../features/Insights/useInsightsChatSend';
import {isRemoteThreadHistoryStale} from '../../features/Insights/insightsThreadHistoryUtils';
import {isInGroupDefinitionMode, isInClarificationPickerMode} from '../../features/Insights/chatRequestUtils';
import ChatHistoryPanel from '../../features/Insights/ChatHistoryPanel';
import MicButton from '../SpeechInput/MicButton';
import {useVoiceInput} from '../SpeechInput/useVoiceInput';

const SESSION_KEY = 'chat_session_id';
const THREAD_KEY = 'chat_thread_id';

/**
 * Isolated input bar so keystrokes don't re-render the entire ChatPanel
 * (which includes the chat history and group definition options).
 */
const ChatPanelInputBar = React.memo(function ChatPanelInputBar({
  onSend,
  isLoading,
  isGroupDefMode,
  isPickerActive,
  onAbort,
  onStop,
  onTextInput,
  clearTextTrigger,
  voiceMode,
  voicePreview,
  voiceWarning,
  micButtonProps,
}) {
  const [inputValue, setInputValue] = useState('');
  const textFieldRef = useRef(null);
  const lastSentRef = useRef('');

  // Clear text when picker selection is made (it's one or the other)
  useEffect(() => {
    if (clearTextTrigger) setInputValue('');
  }, [clearTextTrigger]);

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;
    const msg = inputValue.trim();
    lastSentRef.current = msg;
    setInputValue('');
    onSend(msg);
  };

  // Restore the just-sent text so the user can edit/resend after cancelling.
  const handleStop = () => {
    onStop();
    if (lastSentRef.current) setInputValue(lastSentRef.current);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAbort = () => {
    setInputValue('');
    onAbort();
  };

  return (
    <Box
      sx={{
        p: {xs: 1.5, sm: 2},
        pb: {xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 2},
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e0e0e0',
        flexShrink: 0,
      }}
    >
      {isPickerActive || isGroupDefMode ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            p: 1.5,
            bgcolor: '#fff8e1',
            border: '1px solid #ffd54f',
            borderRadius: 1,
          }}
        >
          <Box
            sx={{display: 'flex', alignItems: 'center', gap: 1, color: '#6d4c00', fontSize: '0.85rem', fontWeight: 500}}
          >
            <Typography component='span' sx={{color: '#d97706', fontSize: '1rem', lineHeight: 1}}>
              ↑
            </Typography>
            <Typography component='span' sx={{fontSize: '0.85rem', fontWeight: 500, color: '#6d4c00'}}>
              Resolve the clarification above to continue
            </Typography>
          </Box>
          <Button
            variant='outlined'
            size='small'
            onClick={handleAbort}
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              borderColor: '#999',
              color: '#555',
              '&:hover': {borderColor: '#555', bgcolor: '#f5f5f5'},
            }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          {voiceWarning && (
            <Typography variant='caption' sx={{color: 'warning.main'}}>
              {voiceWarning}
            </Typography>
          )}
          <TextField
            inputRef={textFieldRef}
            fullWidth
            sx={{minWidth: 0}}
            multiline
            maxRows={4}
            placeholder='Ask a question...'
            value={voiceMode ? voicePreview : inputValue}
            onChange={(e) => {
              if (!voiceMode) setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            disabled={isLoading || voiceMode}
            variant='outlined'
            size='small'
          />
          <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end'}}>
            <MicButton {...micButtonProps(inputValue, setInputValue, isLoading)} />
            {!voiceMode &&
              (isLoading ? (
                <Button
                  variant='text'
                  color='inherit'
                  onClick={handleStop}
                  sx={{
                    width: '100px',
                    flexShrink: 0,
                    textTransform: 'none',
                    color: '#555',
                    border: 'none',
                    '&:hover': {bgcolor: '#f5f5f5', border: 'none'},
                    '&:focus, &:focus-visible, &.Mui-focusVisible, &:active': {
                      outline: '2px solid #9e9e9e',
                      outlineOffset: '2px',
                      border: 'none',
                      boxShadow: 'none',
                      bgcolor: '#f5f5f5',
                    },
                  }}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleSend}
                  disabled={!inputValue.trim()}
                  sx={{width: '100px', flexShrink: 0}}
                  startIcon={<SendIcon />}
                >
                  Send
                </Button>
              ))}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          mt: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '0.8125rem',
          color: '#444',
          lineHeight: 1.5,
        }}
      >
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: '2px',
            bgcolor: '#6750a4',
            color: '#fff',
            borderRadius: '999px',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            lineHeight: 1.5,
          }}
        >
          Beta
        </Box>
        Insights is powered by AI and can make mistakes. Please verify responses.
      </Box>
    </Box>
  );
});

const MIN_PANEL_HEIGHT = 200;
const TARGET_MIN_WIDTH = 300;

function getSidebarRightBound() {
  const el = document.getElementById('accordionSidebar');
  if (!el) {
    return 0;
  }
  const r = el.getBoundingClientRect();
  return Math.max(0, r.right);
}

/**
 * Keep the panel in the viewport to the right of the sidebar rail.
 * @param {number} left
 * @param {number} top
 * @param {number} width
 * @param {number} height
 * @return {{left: number, top: number, width: number, height: number}}
 */
function clampChatPanelLayout(left, top, width, height) {
  const iw = window.innerWidth;
  const ih = window.innerHeight;
  const minLeft = getSidebarRightBound();
  const maxW = Math.max(0, iw - minLeft);
  const low = Math.min(TARGET_MIN_WIDTH, maxW);
  const w = Math.max(low, Math.min(maxW, width));

  let l = Math.min(Math.max(left, minLeft), iw - w);
  l = Math.max(minLeft, l);

  const maxTop = Math.max(0, ih - MIN_PANEL_HEIGHT);
  const t = Math.min(Math.max(top, 0), maxTop);

  const availH = ih - t;
  const h = Math.min(Math.max(height, Math.min(MIN_PANEL_HEIGHT, availH)), availH);

  return {left: l, top: t, width: w, height: h};
}

/**
 * Resizable, draggable floating Insights chat; shares Redux `chat` slice with the Insights page.
 * @param {Object} props
 * @param {function(): void} [props.onScreenshot]
 * @return {JSX.Element|null}
 */
function ChatPanel({onScreenshot}) {
  const dispatch = useDispatch();
  const {voiceMode, voicePreview, voiceWarning, micButtonProps} = useVoiceInput();
  const chatState = useSelector((state) => {
    return state.chat;
  });
  const {sendMessage, stopMessage, isLoading} = useInsightsChatSend();

  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const chatEndRef = useRef(null);
  /** @type {React.MutableRefObject<null | {type: 'width' | 'top' | 'bottom' | 'move'; startX: number; startY: number; startLeft: number; startTop: number; startWidth: number; startHeight: number}>} */
  const resizeRef = useRef(null);
  const [resizeMode, setResizeMode] = useState(/** @type {null | 'width' | 'top' | 'bottom' | 'move'} */ (null));
  const panelRef = useRef(null);
  const layoutRef = useRef({
    panelLeft: chatState.panelLeft,
    panelTop: chatState.panelTop,
    panelWidth: chatState.panelWidth,
    panelHeight: chatState.panelHeight,
  });

  layoutRef.current = {
    panelLeft: chatState.panelLeft,
    panelTop: chatState.panelTop,
    panelWidth: chatState.panelWidth,
    panelHeight: chatState.panelHeight,
  };

  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    }
    let currentThreadId = sessionStorage.getItem(THREAD_KEY);
    if (!currentThreadId) {
      currentThreadId = Date.now().toString();
      sessionStorage.setItem(THREAD_KEY, currentThreadId);
    }
    dispatch(setActiveThreadId(currentThreadId));
    fetch('/insights/suggested-prompts', {credentials: 'include'})
      .then((r) => {
        return r.ok ? r.json() : [];
      })
      .then(setSuggestedPrompts)
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    const ac = new AbortController();
    const loadThreadHistory = async () => {
      const threadId = sessionStorage.getItem(THREAD_KEY);
      if (!threadId) return;

      try {
        const response = await fetch(`/insights/thread/${encodeURIComponent(threadId)}/history`, {
          credentials: 'include',
          signal: ac.signal,
        });

        if (!response.ok) return;

        const data = await response.json();
        if (!data.chatHistory || data.chatHistory.length === 0) return;

        const latest = chatStateRef.current;
        if (latest.activeThreadId && latest.activeThreadId !== threadId) {
          return;
        }
        if (isRemoteThreadHistoryStale(latest.chatHistory, data.chatHistory)) {
          return;
        }

        dispatch(
          loadThread({
            threadId: data.threadId,
            chatHistory: data.chatHistory,
          })
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Failed to load thread history:', error);
      }
    };

    loadThreadHistory();
    return () => {
      ac.abort();
    };
  }, [dispatch]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [chatState.chatHistory]);

  const applyClampedLayout = useCallback(() => {
    const {panelLeft, panelTop, panelWidth, panelHeight} = layoutRef.current;
    const next = clampChatPanelLayout(panelLeft, panelTop, panelWidth, panelHeight);
    if (next.left !== panelLeft || next.top !== panelTop || next.width !== panelWidth || next.height !== panelHeight) {
      dispatch(setPanelFrame(next));
    }
  }, [dispatch]);

  useEffect(() => {
    const onReclamp = () => {
      applyClampedLayout();
    };
    window.addEventListener('resize', onReclamp);
    const el = document.getElementById('accordionSidebar');
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(onReclamp) : null;
    if (el && ro) {
      ro.observe(el);
    }
    onReclamp();
    return () => {
      window.removeEventListener('resize', onReclamp);
      ro?.disconnect();
    };
  }, [applyClampedLayout]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const ctx = resizeRef.current;
      if (!ctx) return;

      if (ctx.type === 'move') {
        const left = ctx.startLeft + (e.clientX - ctx.startX);
        const top = ctx.startTop + (e.clientY - ctx.startY);
        const next = clampChatPanelLayout(left, top, ctx.startWidth, ctx.startHeight);
        dispatch(setPanelFrame(next));
        return;
      }

      if (ctx.type === 'width') {
        const left = ctx.startLeft + (e.clientX - ctx.startX);
        const width = ctx.startWidth - (e.clientX - ctx.startX);
        const next = clampChatPanelLayout(left, ctx.startTop, width, ctx.startHeight);
        dispatch(setPanelFrame(next));
        return;
      }

      if (ctx.type === 'top') {
        const top = ctx.startTop + (e.clientY - ctx.startY);
        const height = ctx.startHeight - (e.clientY - ctx.startY);
        const next = clampChatPanelLayout(ctx.startLeft, top, ctx.startWidth, height);
        dispatch(setPanelFrame(next));
        return;
      }

      if (ctx.type === 'bottom') {
        const height = ctx.startHeight + (e.clientY - ctx.startY);
        const next = clampChatPanelLayout(ctx.startLeft, ctx.startTop, ctx.startWidth, height);
        dispatch(setPanelFrame(next));
      }
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      setResizeMode(null);
    };

    if (resizeMode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeMode, dispatch]);

  const handleHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, a, [role="button"], .MuiIconButton-root, .MuiButton-root')) {
      return;
    }
    e.preventDefault();
    const {panelLeft, panelTop, panelWidth, panelHeight} = chatState;
    resizeRef.current = {
      type: 'move',
      startX: e.clientX,
      startY: e.clientY,
      startLeft: panelLeft,
      startTop: panelTop,
      startWidth: panelWidth,
      startHeight: panelHeight,
    };
    setResizeMode('move');
  };

  const chatStateRef = useRef(chatState);
  chatStateRef.current = chatState;

  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const handleInputSend = useCallback(async (userMessage) => {
    if (!userMessage) return;
    const currentSessionId = sessionStorage.getItem(SESSION_KEY);
    const currentThreadId = sessionStorage.getItem(THREAD_KEY);
    const latest = chatStateRef.current;
    await sendMessageRef.current(
      userMessage,
      latest.chatHistory,
      latest.constantOverrides,
      currentSessionId,
      currentThreadId
    );
  }, []);

  const isGroupDefMode = useMemo(() => {
    return isInGroupDefinitionMode(chatState.chatHistory, chatState.textDefinitionMode);
  }, [
    chatState.chatHistory.length,
    chatState.chatHistory[chatState.chatHistory.length - 1]?.mode,
    chatState.textDefinitionMode,
  ]);

  const isPickerActive = useMemo(() => {
    return isInClarificationPickerMode(chatState.chatHistory) && !chatState.textDefinitionMode;
  }, [
    chatState.chatHistory.length,
    chatState.chatHistory[chatState.chatHistory.length - 1]?.mode,
    chatState.textDefinitionMode,
  ]);

  const handleAbortGroupDefinition = useCallback(async () => {
    const currentSessionId = sessionStorage.getItem(SESSION_KEY);
    const currentThreadId = sessionStorage.getItem(THREAD_KEY);
    const latest = chatStateRef.current;
    await sendMessageRef.current(
      '[Group definition cancelled]',
      latest.chatHistory,
      latest.constantOverrides,
      currentSessionId,
      currentThreadId,
      null,
      true
    );
  }, []);

  const handleSendPrompt = useCallback(async (text) => {
    const currentSessionId = sessionStorage.getItem(SESSION_KEY);
    const currentThreadId = sessionStorage.getItem(THREAD_KEY);
    const latest = chatStateRef.current;
    await sendMessageRef.current(text, latest.chatHistory, latest.constantOverrides, currentSessionId, currentThreadId);
  }, []);

  const handleClarificationSubmit = async (selectedItems) => {
    if (isLoading || selectedItems.length === 0) return;
    const currentSessionId = sessionStorage.getItem(SESSION_KEY);
    const currentThreadId = sessionStorage.getItem(THREAD_KEY);
    const isStructured = selectedItems.length > 0 && typeof selectedItems[0] === 'object' && selectedItems[0] !== null;
    if (isStructured) {
      const counts = {};
      for (const item of selectedItems) {
        const dim = item.type || 'item';
        counts[dim] = (counts[dim] || 0) + 1;
      }
      const responseText = selectedItems
        .map((item) => {
          return item.submitLabel || item.label || '';
        })
        .join(', ');
      await sendMessage(
        responseText,
        chatState.chatHistory,
        chatState.constantOverrides,
        currentSessionId,
        currentThreadId,
        selectedItems
      );
    } else {
      const responseText = selectedItems.join(', ');
      await sendMessage(
        responseText,
        chatState.chatHistory,
        chatState.constantOverrides,
        currentSessionId,
        currentThreadId
      );
    }
  };

  /**
   * @param {Object} msg
   * @param {number} index
   * @return {JSX.Element}
   */
  function mapHistoryToMessage(msg, index) {
    const currentThreadId = sessionStorage.getItem(THREAD_KEY) || chatState.activeThreadId;
    return (
      <InsightsChatMessage
        key={index}
        msg={msg}
        index={index}
        layout='panel'
        threadId={currentThreadId}
        onClarificationSubmit={handleClarificationSubmit}
        onAbortGroupDefinition={handleAbortGroupDefinition}
      />
    );
  }

  return (
    <>
      <ChatHistoryPanel open={chatState.historyPanelOpen} />
      <Box
        ref={panelRef}
        className='chat-panel'
        sx={{
          position: 'fixed',
          left: `${chatState.panelLeft}px`,
          top: `${chatState.panelTop}px`,
          width: `${chatState.panelWidth}px`,
          height: `${chatState.panelHeight}px`,
          maxWidth: '100vw',
          boxSizing: 'border-box',
          paddingTop: '4px',
          paddingBottom: '4px',
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1300,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
        }}
      >
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const {panelLeft, panelTop, panelWidth, panelHeight} = chatState;
            resizeRef.current = {
              type: 'top',
              startX: e.clientX,
              startY: e.clientY,
              startLeft: panelLeft,
              startTop: panelTop,
              startWidth: panelWidth,
              startHeight: panelHeight,
            };
            setResizeMode('top');
          }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: '4px',
            cursor: 'row-resize',
            backgroundColor: 'transparent',
            '&:hover': {backgroundColor: '#1976d2'},
            zIndex: 6,
            display: {xs: 'none', sm: 'block'},
          }}
        />
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const {panelLeft, panelTop, panelWidth, panelHeight} = chatState;
            resizeRef.current = {
              type: 'bottom',
              startX: e.clientX,
              startY: e.clientY,
              startLeft: panelLeft,
              startTop: panelTop,
              startWidth: panelWidth,
              startHeight: panelHeight,
            };
            setResizeMode('bottom');
          }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '4px',
            cursor: 'row-resize',
            backgroundColor: 'transparent',
            '&:hover': {backgroundColor: '#1976d2'},
            zIndex: 6,
            display: {xs: 'none', sm: 'block'},
          }}
        />
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const {panelLeft, panelTop, panelWidth, panelHeight} = chatState;
            resizeRef.current = {
              type: 'width',
              startX: e.clientX,
              startY: e.clientY,
              startLeft: panelLeft,
              startTop: panelTop,
              startWidth: panelWidth,
              startHeight: panelHeight,
            };
            setResizeMode('width');
          }}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            '&:hover': {backgroundColor: '#1976d2'},
            zIndex: 7,
            display: {xs: 'none', sm: 'block'},
          }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            onMouseDown={handleHeaderMouseDown}
            sx={{
              p: {xs: 1.5, sm: 2},
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              cursor: resizeMode === 'move' ? 'grabbing' : 'grab',
              flexShrink: 0,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
              <Typography variant='h6' sx={{minWidth: 0, fontSize: {xs: '1rem', sm: undefined}, userSelect: 'none'}}>
                AI Insights
              </Typography>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1,
                  py: '3px',
                  bgcolor: '#6750a4',
                  color: '#fff',
                  borderRadius: '999px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                Beta
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: 'flex-end',
                flex: {xs: '1 1 100%', sm: 'none'},
                cursor: 'default',
              }}
              onMouseDown={(ev) => {
                ev.stopPropagation();
              }}
            >
              <Tooltip
                title={
                  chatState.overlayDimEnabled
                    ? 'Turn off dim — full page visible and interactive (close from the header)'
                    : 'Turn on dim — darken page; click outside to close'
                }
              >
                <IconButton
                  size='small'
                  aria-pressed={chatState.overlayDimEnabled}
                  aria-label={chatState.overlayDimEnabled ? 'Turn off background dim' : 'Turn on background dim'}
                  onClick={() => {
                    dispatch(setChatOverlayDimEnabled(!chatState.overlayDimEnabled));
                  }}
                  sx={{
                    color: chatState.overlayDimEnabled ? '#1976d2' : '#666',
                    '&:hover': {backgroundColor: 'rgba(0,0,0,0.05)'},
                  }}
                >
                  {chatState.overlayDimEnabled ? (
                    <LightbulbIcon fontSize='small' />
                  ) : (
                    <LightbulbOutlinedIcon fontSize='small' />
                  )}
                </IconButton>
              </Tooltip>
              <Button
                variant='text'
                size='small'
                startIcon={<HistoryIcon />}
                onClick={() => {
                  dispatch(setHistoryPanelOpen(true));
                }}
                disabled={isLoading}
              >
                History
              </Button>
              <Button
                variant='text'
                size='small'
                onClick={() => {
                  const newThreadId = Date.now().toString();
                  sessionStorage.setItem(THREAD_KEY, newThreadId);
                  dispatch(setActiveThreadId(newThreadId));
                  dispatch(clearChatHistory());
                }}
                disabled={isLoading}
              >
                Clear
              </Button>
              <Tooltip title='Close Chat'>
                <IconButton
                  size='small'
                  onClick={() => {
                    return dispatch(setChatOpen(false));
                  }}
                  sx={{
                    color: '#666',
                    '&:hover': {backgroundColor: 'rgba(0,0,0,0.05)'},
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: {xs: 1.5, sm: 2},
              backgroundColor: '#fafafa',
            }}
          >
            {chatState.chatHistory.length === 0 ? (
              <Typography variant='body2' color='text.secondary' align='center' sx={{mt: 4}}>
                Start a conversation by typing a message below
              </Typography>
            ) : (
              chatState.chatHistory.map(mapHistoryToMessage)
            )}
            <div ref={chatEndRef} />
          </Box>

          {chatState.chatHistory.length === 0 && suggestedPrompts.length > 0 && (
            <Box sx={{px: {xs: 1.5, sm: 2}, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0}}>
              {suggestedPrompts.map((prompt) => {
                return (
                  <Button
                    key={prompt}
                    variant='outlined'
                    size='small'
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => {
                      handleSendPrompt(prompt);
                    }}
                    disabled={isLoading || voiceMode}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      width: '100%',
                      py: 1,
                      px: 1.5,
                      borderRadius: 2,
                      borderColor: '#e0e0e0',
                      color: 'text.primary',
                      fontSize: '0.8rem',
                      '&:hover': {
                        borderColor: '#1976d2',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    {prompt}
                  </Button>
                );
              })}
            </Box>
          )}
          <ChatPanelInputBar
            onSend={handleInputSend}
            isLoading={isLoading}
            isGroupDefMode={isGroupDefMode}
            isPickerActive={isPickerActive}
            onAbort={handleAbortGroupDefinition}
            onStop={stopMessage}
            onTextInput={() => {
              dispatch(setTextDefinitionMode(true));
            }}
            clearTextTrigger={chatState.clearTextTrigger || 0}
            voiceMode={voiceMode}
            voicePreview={voicePreview}
            voiceWarning={voiceWarning}
            micButtonProps={micButtonProps}
          />
        </Box>
      </Box>
    </>
  );
}

export default ChatPanel;
